import React, {useState} from 'react';
import {Button, Input, Select} from 'antd';
import {useTranslation} from 'react-i18next';
import {
  PodExemptReportFiltersInput,
  PodExemptStatus,
} from '../../graphql/podExempt/types';
import {POD_EXEMPT_STATUS_I18N} from './podExemptConstants';

interface Props {
  filters: PodExemptReportFiltersInput;
  onFiltersChange: (filters: PodExemptReportFiltersInput) => void;
  /** 地址类型下拉选项（字典），审核端也可传 */
  addressTypeOptions?: {value: string; label: string}[];
  /** 按钮行左侧插槽（DSP 端放「新建上报」） */
  actionSlot?: React.ReactNode;
  /** 「重置」回到的基线（初审/终审页 = 各自的待审状态）；不传则重置为空 */
  defaultFilters?: PodExemptReportFiltersInput;
}

const STATUS_OPTIONS: PodExemptStatus[] = [
  'PENDING_FIRST',
  'PENDING_FINAL',
  'EFFECTIVE',
  'REJECTED',
  'CLOSED',
  'OFFLINE',
];

/**
 * 表单式筛选区（对齐原型）：上报ID / 地址÷围栏 / 地址类型 / 状态 四项，
 * 点「筛选」才生效，按钮行右对齐，左侧留 actionSlot 给「新建上报」。
 */
const PodExemptFilterBar: React.FC<Props> = ({
  filters,
  onFiltersChange,
  addressTypeOptions,
  actionSlot,
  defaultFilters,
}) => {
  const {t} = useTranslation();
  const [draft, setDraft] = useState<PodExemptReportFiltersInput>(filters);

  const labelCls = 'flex items-center gap-2 text-[13px]';
  const labelSpan = (text: string) => (
    <span className="shrink-0 w-16 text-right text-gray-500">{text}</span>
  );

  return (
    <div>
      <div className="grid grid-cols-4 gap-x-5 gap-y-3">
        <label className={labelCls}>
          {labelSpan(t('podExempt.filter.reportId'))}
          <Input
            allowClear
            placeholder={t('podExempt.filter.inputPlaceholder') ?? ''}
            value={draft.keyword ?? ''}
            onChange={(e) => setDraft({...draft, keyword: e.target.value || null})}
          />
        </label>
        <label className={labelCls}>
          {labelSpan(t('podExempt.field.addressOrFence'))}
          <Input
            allowClear
            placeholder={t('podExempt.filter.inputPlaceholder') ?? ''}
            value={draft.addressKeyword ?? ''}
            onChange={(e) => setDraft({...draft, addressKeyword: e.target.value || null})}
          />
        </label>
        <label className={labelCls}>
          {labelSpan(t('podExempt.field.addressType'))}
          <Select<string>
            allowClear
            className="flex-1 min-w-0"
            placeholder={t('podExempt.filter.selectPlaceholder')}
            value={draft.addressType ?? undefined}
            onChange={(value) => setDraft({...draft, addressType: value || null})}
            options={addressTypeOptions ?? []}
          />
        </label>
        <label className={labelCls}>
          {labelSpan(t('podExempt.field.status'))}
          <Select<PodExemptStatus | ''>
            className="flex-1 min-w-0"
            value={(draft.statuses?.[0] ?? '') as PodExemptStatus | ''}
            onChange={(value) => setDraft({...draft, statuses: value ? [value] : null})}
            options={[
              {value: '', label: t('podExempt.filter.allStatus')},
              ...STATUS_OPTIONS.map((status) => ({
                value: status,
                label: t(POD_EXEMPT_STATUS_I18N[status]),
              })),
            ]}
          />
        </label>
      </div>
      <div className="flex items-center gap-2.5 py-4">
        <div className="mr-auto">{actionSlot}</div>
        <Button type="primary" onClick={() => onFiltersChange({...draft, keyword: draft.keyword?.trim() || null, addressKeyword: draft.addressKeyword?.trim() || null})}>
          {t('podExempt.filter.search')}
        </Button>
        <Button
          onClick={() => {
            // 重置回基线而非清空：初审/终审页的默认「待审」筛选是工作台语义，清空会混进历史单
            const base = defaultFilters ?? {};
            setDraft(base);
            onFiltersChange(base);
          }}
        >
          {t('podExempt.filter.reset')}
        </Button>
      </div>
    </div>
  );
};

export default PodExemptFilterBar;
